// Utilidades del modal de escaneo en aprobación de refunds:
// cámara, lectura de barcodes, match de líneas y tamaño del modal.
import { getRefundLineStatusKey } from '../../constants/refundLineStatus'

// --- Formatos y opciones del lector de barcodes ---
export const NATIVE_BARCODE_FORMATS = [
  'ean_13',
  'ean_8',
  'upc_a',
  'upc_e',
  'code_128',
  'code_39',
  'code_93',
  'itf',
  'codabar'
]

export const BARCODE_READER_OPTIONS = {
  formats: ['AllLinear'],
  tryHarder: true,
  tryRotate: true,
  tryInvert: true,
  tryDownscale: true,
  downscaleFactor: 2,
  downscaleThreshold: 300,
  minLineCount: 1,
  maxNumberOfSymbols: 1
}

// --- Cámara: constraints, device preferido y región de escaneo ---
export const REFUND_SCAN_CAMERA_CONSTRAINTS = {
  video: {
    facingMode: { ideal: 'environment' },
    width: { ideal: 1920 },
    height: { ideal: 1080 },
    frameRate: { ideal: 30, max: 30 },
    advanced: [
      { focusMode: 'continuous' },
      { exposureMode: 'continuous' },
      { whiteBalanceMode: 'continuous' }
    ]
  },
  audio: false
}

/** Constraints de getUserMedia; si hay deviceId fija esa cámara. */
export const getRefundScanCameraConstraints = (deviceId = '') => {
  if (!deviceId) return REFUND_SCAN_CAMERA_CONSTRAINTS

  const { facingMode, ...videoConstraints } = REFUND_SCAN_CAMERA_CONSTRAINTS.video

  return {
    video: {
      ...videoConstraints,
      deviceId: { exact: deviceId }
    },
    audio: false
  }
}

const BACK_CAMERA_LABEL_REGEX = /(back|rear|environment|trasera|traseira|posterior)/i
const MACRO_CAMERA_LABEL_REGEX = /macro/i
const ULTRA_WIDE_CAMERA_LABEL_REGEX = /(ultra\s*wide|ultrawide|ultra gran|0\.5x)/i

/** Score más bajo = mejor (prioriza trasera estándar; penaliza macro y ultra-wide). */
const getCameraPreferenceScore = (device = {}) => {
  const label = device.label || ''
  let score = 0

  if (BACK_CAMERA_LABEL_REGEX.test(label)) score -= 20
  if (MACRO_CAMERA_LABEL_REGEX.test(label)) score += 15
  if (ULTRA_WIDE_CAMERA_LABEL_REGEX.test(label)) score += 30

  return score
}

/** Ordena cámaras: trasera estándar primero; macro y ultra-wide al final. */
export const getPreferredVideoInputDevices = (devices = []) => {
  const videoInputs = devices.filter((device) => device?.kind === 'videoinput' && device.deviceId)
  const backCameras = videoInputs.filter((device) =>
    BACK_CAMERA_LABEL_REGEX.test(device.label || '')
  )

  return (backCameras.length > 0 ? backCameras : videoInputs)
    .slice()
    .sort((left, right) => getCameraPreferenceScore(left) - getCameraPreferenceScore(right))
}

/** Fracción del viewport donde se busca el barcode (overlay de escaneo). */
export const REFUND_SCAN_REGION = {
  left: 0.08,
  top: 0.36,
  width: 0.84,
  height: 0.34
}

/** Traduce la región visible del overlay a coordenadas del frame de video. */
export const getBarcodeScanRegion = ({
  videoWidth,
  videoHeight,
  displayWidth,
  displayHeight
}) => {
  if (!videoWidth || !videoHeight) return null

  if (!displayWidth || !displayHeight) {
    return {
      x: Math.round(videoWidth * REFUND_SCAN_REGION.left),
      y: Math.round(videoHeight * REFUND_SCAN_REGION.top),
      width: Math.round(videoWidth * REFUND_SCAN_REGION.width),
      height: Math.round(videoHeight * REFUND_SCAN_REGION.height)
    }
  }

  const coverScale = Math.max(displayWidth / videoWidth, displayHeight / videoHeight)
  const renderedWidth = videoWidth * coverScale
  const renderedHeight = videoHeight * coverScale
  const horizontalCrop = (renderedWidth - displayWidth) / 2
  const verticalCrop = (renderedHeight - displayHeight) / 2

  return {
    x: Math.max(
      0,
      Math.round((displayWidth * REFUND_SCAN_REGION.left + horizontalCrop) / coverScale)
    ),
    y: Math.max(
      0,
      Math.round((displayHeight * REFUND_SCAN_REGION.top + verticalCrop) / coverScale)
    ),
    width: Math.min(
      videoWidth,
      Math.round((displayWidth * REFUND_SCAN_REGION.width) / coverScale)
    ),
    height: Math.min(
      videoHeight,
      Math.round((displayHeight * REFUND_SCAN_REGION.height) / coverScale)
    )
  }
}

const clampCameraValue = (value, range = {}) => {
  const min = typeof range.min === 'number' ? range.min : value
  const max = typeof range.max === 'number' ? range.max : value
  return Math.min(max, Math.max(min, value))
}

const PREFERRED_CAMERA_ZOOM = 1

const pickCameraZoom = (zoomCapabilities) => {
  if (!zoomCapabilities || typeof zoomCapabilities.max !== 'number') return null

  const min = typeof zoomCapabilities.min === 'number' ? zoomCapabilities.min : 1
  const max = zoomCapabilities.max
  if (max <= min) return null

  const zoom = clampCameraValue(PREFERRED_CAMERA_ZOOM, zoomCapabilities)
  const step =
    typeof zoomCapabilities.step === 'number' && zoomCapabilities.step > 0
      ? zoomCapabilities.step
      : 0

  if (!step) return zoom

  return Number((Math.round((zoom - min) / step) * step + min).toFixed(2))
}

const supportsConstraintValue = (capability, value) =>
  Array.isArray(capability) && capability.includes(value)

// --- BarcodeDetector nativo del navegador (fallback si no hay lib JS) ---
export const createNativeBarcodeDetector = async () => {
  const NativeBarcodeDetector = globalThis.BarcodeDetector
  if (!NativeBarcodeDetector) return null

  try {
    let formats = NATIVE_BARCODE_FORMATS

    if (typeof NativeBarcodeDetector.getSupportedFormats === 'function') {
      const supportedFormats = await NativeBarcodeDetector.getSupportedFormats()
      formats = NATIVE_BARCODE_FORMATS.filter((format) => supportedFormats.includes(format))
    }

    if (formats.length > 0) {
      return new NativeBarcodeDetector({ formats })
    }

    return new NativeBarcodeDetector()
  } catch (_) {
    return null
  }
}

export const readNativeBarcodeValues = async (detector, source) => {
  if (!detector?.detect || !source) return []

  try {
    const results = await detector.detect(source)
    return results.map((result) => result?.rawValue || result?.text).filter(Boolean)
  } catch (_) {
    return []
  }
}

const pickNearFocusDistance = (focusDistanceCapabilities) => {
  if (
    !focusDistanceCapabilities ||
    typeof focusDistanceCapabilities.min !== 'number' ||
    typeof focusDistanceCapabilities.max !== 'number' ||
    focusDistanceCapabilities.max <= focusDistanceCapabilities.min
  ) {
    return null
  }

  const value =
    focusDistanceCapabilities.min +
    (focusDistanceCapabilities.max - focusDistanceCapabilities.min) * 0.2
  const step =
    typeof focusDistanceCapabilities.step === 'number' && focusDistanceCapabilities.step > 0
      ? focusDistanceCapabilities.step
      : 0

  if (!step) return value

  return Number(
    (
      Math.round((value - focusDistanceCapabilities.min) / step) * step +
      focusDistanceCapabilities.min
    ).toFixed(2)
  )
}

/** Arma constraints avanzados (zoom, exposición, foco) según capabilities del track. */
export const getCameraTrackEnhancementConstraints = (capabilities = {}, options = {}) => {
  const constraints = []
  const supportsSingleShotFocus =
    options.refocus && supportsConstraintValue(capabilities.focusMode, 'single-shot')
  const preferredFocusMode =
    options.refocus && supportsSingleShotFocus ? 'single-shot' : 'continuous'

  const zoom = pickCameraZoom(capabilities.zoom)
  if (!options.focusOnly && typeof zoom === 'number') {
    constraints.push({ zoom })
  }

  if (!options.focusOnly && supportsConstraintValue(capabilities.exposureMode, 'continuous')) {
    constraints.push({ exposureMode: 'continuous' })
  }

  if (!options.focusOnly && supportsConstraintValue(capabilities.whiteBalanceMode, 'continuous')) {
    constraints.push({ whiteBalanceMode: 'continuous' })
  }

  if (options.refocus) {
    constraints.push({ pointsOfInterest: [{ x: 0.5, y: 0.5 }] })
  }

  if (
    options.refocus &&
    !supportsSingleShotFocus &&
    supportsConstraintValue(capabilities.focusMode, 'manual')
  ) {
    const focusDistance = pickNearFocusDistance(capabilities.focusDistance)
    if (typeof focusDistance === 'number') {
      constraints.push({ focusMode: 'manual' })
      constraints.push({ focusDistance })
    }
  }

  // Focus last because changing zoom/exposure can invalidate a focus lock on Android cameras.
  if (supportsConstraintValue(capabilities.focusMode, preferredFocusMode)) {
    constraints.push({ focusMode: preferredFocusMode })
  }

  return constraints
}

/** Aplica mejoras al MediaStreamTrack; ignora constraints que el browser reporte pero no soporte. */
export const applyCameraTrackEnhancements = async (track, options = {}) => {
  if (!track?.applyConstraints) return false

  const capabilities = track.getCapabilities?.() || {}
  const constraints = getCameraTrackEnhancementConstraints(capabilities, options)
  let didApplyConstraint = false

  for (const constraint of constraints) {
    try {
      await track.applyConstraints({ advanced: [constraint] })
      didApplyConstraint = true
    } catch (_) {
      // Some Android browsers report capabilities optimistically; keep the camera alive.
    }
  }

  return didApplyConstraint
}

// --- Match del código escaneado con líneas del refund ---
export const INTEGER_INPUT_REGEX = /^\d*$/
export const DECIMAL_INPUT_REGEX = /^\d*\.?\d{0,2}$/

export const lineAllowsDecimals = (line) => {
  const quantity = Number(line?.original_quantity ?? line?.quantity)
  return Number.isFinite(quantity) && !Number.isInteger(quantity)
}

export const getQuantityInputRegex = (line) =>
  lineAllowsDecimals(line) ? DECIMAL_INPUT_REGEX : INTEGER_INPUT_REGEX

export const parseVerifiedQuantity = (value, allowDecimals = false) => {
  const trimmed = String(value ?? '').trim()
  if (!trimmed || trimmed === '.') return Number.NaN

  return allowDecimals ? Number(trimmed) : Number.parseInt(trimmed, 10)
}

export const getLineDefaultQuantity = (line) => {
  if (line?.verified_quantity == null) return ''

  const quantity = Number(line.verified_quantity)
  if (!Number.isFinite(quantity)) return ''

  return Number.isInteger(quantity) ? String(quantity) : quantity.toFixed(2)
}

export const isProcessedRescanLine = (line = {}) => getRefundLineStatusKey(line) !== 'pending'

export const normalizeCode = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()

/** Variantes UPC/EAN (12↔13 dígitos, sin guiones) para tolerar lecturas distintas. */
export const getCodeSearchVariants = (value) => {
  const normalized = normalizeCode(value)
  if (!normalized) return []

  const variants = new Set([normalized])
  const compact = normalized.replace(/[\s-]+/g, '')
  if (compact) variants.add(compact)

  const digitsOnly = compact.replace(/\D/g, '')
  if (digitsOnly && digitsOnly === compact) {
    variants.add(digitsOnly)

    if (digitsOnly.length === 12) {
      variants.add(`0${digitsOnly}`)
    }

    if (digitsOnly.length === 13 && digitsOnly.startsWith('0')) {
      variants.add(digitsOnly.slice(1))
    }
  }

  return Array.from(variants)
}

export const getLineBarcodeValues = (barcode = '') =>
  String(barcode || '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean)

export const getPrimaryBarcode = (barcode = '') => getLineBarcodeValues(barcode)[0] || ''

/** Campos de la línea contra los que se busca el código escaneado. */
export const getLineSearchValues = (line = {}) =>
  [
    ...getLineBarcodeValues(line.product_barcode),
    line.product_db_ref,
    line.refund_item_uid,
    line.product_name
  ]
    .filter(Boolean)
    .map((value) => String(value))

const codeValuesMatch = (lineValue, rawValue, { allowPartial = true } = {}) => {
  const lineVariants = getCodeSearchVariants(lineValue)
  const queryVariants = getCodeSearchVariants(rawValue)
  if (!lineVariants.length || !queryVariants.length) return false

  if (lineVariants.some((lineVariant) => queryVariants.includes(lineVariant))) return true
  if (!allowPartial) return false

  return lineVariants.some((lineVariant) =>
    queryVariants.some(
      (queryVariant) => lineVariant.includes(queryVariant) || queryVariant.includes(lineVariant)
    )
  )
}

/** Busca todas las líneas del refund que coinciden con el código escaneado o tipeado. */
export const findRefundLinesByCode = (lines = [], rawValue, { allowPartial = true } = {}) => {
  if (!normalizeCode(rawValue)) return []

  return lines.filter((line) =>
    getLineSearchValues(line).some((value) => codeValuesMatch(value, rawValue, { allowPartial }))
  )
}

/** Busca la primera línea del refund que coincide con el código escaneado o tipeado. */
export const findRefundLineByCode = (lines = [], rawValue, options = {}) =>
  findRefundLinesByCode(lines, rawValue, options)[0] || null

/** Dimensiones del Dialog según modo (edición / actualizar línea / escaneo). */
export const getModalPaperSize = ({ isEditMode, isUpdatingLine, hasSelectedLine }) => {
  if (isEditMode) {
    return {
      width: { xs: 'calc(100vw - 32px)', sm: 600 },
      height: { xs: 'auto', sm: 440 }
    }
  }

  if (isUpdatingLine) {
    return {
      width: { xs: 'calc(100vw - 32px)', sm: 620 },
      height: { xs: 'auto', sm: 420 }
    }
  }

  return {
    width: { xs: 'calc(100vw - 32px)', sm: 550 },
    height: { xs: 'auto', sm: hasSelectedLine ? 580 : 500 }
  }
}
